import { Matrix4, Quaternion, Vector3 } from 'three'

/**
 * Unity is left-handed (Y up, +Z forward); three.js is right-handed (Y up, -Z
 * forward). Everything in the kit — level JSON, prefab transforms, OBJ meshes —
 * is authored in Unity space, so it has to be mapped once on the way in.
 *
 * The mapping is the mirror `M = diag(1, 1, -1)`:
 *
 *   position   (x, y, z)    →  (x, y, -z)
 *   rotation   R            →  M · R · M
 *   quaternion (x, y, z, w) →  (-x, -y, z, w)
 *
 * The quaternion form falls out of expanding `M · R(q) · M` against the
 * quaternion→matrix formula, which Unity and three.js share verbatim.
 *
 * Cameras need no extra half-turn even though Unity looks down local +Z and
 * three.js looks down local -Z: the Z mirror already accounts for it. Verified
 * against Level 1's `CameraData` — both the forward vector and the up vector
 * come out matching.
 *
 * One consequence to remember when meshes arrive (phase 3): mirroring flips
 * triangle winding, so imported geometry must have its index order reversed or
 * it will render inside-out.
 */

export function unityPosition(x: number, y: number, z: number, out = new Vector3()): Vector3 {
  return out.set(x, y, -z)
}

export function convertPosition(v: Vector3, out = new Vector3()): Vector3 {
  return out.set(v.x, v.y, -v.z)
}

export function unityQuaternion(
  x: number,
  y: number,
  z: number,
  w: number,
  out = new Quaternion(),
): Quaternion {
  return out.set(-x, -y, z, w)
}

export function convertQuaternion(q: Quaternion, out = new Quaternion()): Quaternion {
  return out.set(-q.x, -q.y, q.z, q.w)
}

/** Convert a direction the same way a position is converted. */
export const convertDirection = convertPosition

const _axis = new Vector3()

/**
 * A Unity axis-angle rotation, as a three quaternion.
 *
 * `convertPosition` is `(x, y, -z)` — a **reflection**, not a rotation — so
 * conjugating a Unity rotation by it gives a rotation about the converted axis
 * by the **negated** angle. That is the same rule `convertQuaternion` encodes:
 * for `q = (a·sin(t/2), cos(t/2))`, `(-qx, -qy, qz, qw)` is exactly
 * `axisAngle((ax, ay, -az), -t)`.
 *
 * Kept here, and used rather than open-coded, because dropping that minus is
 * invisible whenever the axis happens to be backwards too — `R(-a, t)` and
 * `R(a, -t)` are the same rotation. `SessionView.applyTip` paid for that once.
 *
 * `degrees`, like every angle the rules produce.
 */
export function convertAxisAngle(
  unityAxis: Vector3,
  degrees: number,
  out = new Quaternion(),
): Quaternion {
  convertPosition(unityAxis, _axis).normalize()
  return out.setFromAxisAngle(_axis, -(degrees * Math.PI) / 180)
}

/**
 * The same rotation, but staying **in Unity space** — for rules that have to
 * rotate a Unity-space point before the view ever sees it.
 *
 * No minus here, and that is not an inconsistency with `convertAxisAngle`.
 * `Quaternion.AngleAxis(t, a)` stores `(a·sin(t/2), cos(t/2))`, so applied to a
 * raw coordinate triple it is the ordinary right-handed rotation by `+t`; the
 * handedness difference between Unity and three lives entirely in
 * `convertPosition`, which is why the angle flips only on the way across. The
 * two agree exactly: `convertPosition(unityAxisAngle(a, t) · v)` equals
 * `convertAxisAngle(a, t) · convertPosition(v)`.
 */
export function unityAxisAngle(
  unityAxis: Vector3,
  degrees: number,
  out = new Quaternion(),
): Quaternion {
  _axis.copy(unityAxis).normalize()
  return out.setFromAxisAngle(_axis, (degrees * Math.PI) / 180)
}

/**
 * `Transform.RotateAround(pivot, axis, degrees)` for a single Unity-space
 * point — a rigid rotation, so the distance to the pivot is preserved.
 */
export function rotateAroundUnity(
  point: Vector3,
  pivot: Vector3,
  unityAxis: Vector3,
  degrees: number,
  out = new Vector3(),
): Vector3 {
  unityAxisAngle(unityAxis, degrees, _spin)
  return out.copy(point).sub(pivot).applyQuaternion(_spin).add(pivot)
}

const _spin = new Quaternion()

const _x = new Vector3()
const _y = new Vector3()
const _z = new Vector3()
const _up = new Vector3(0, 1, 0)
const _basis = new Matrix4()

/**
 * `UnityEngine.Quaternion.LookRotation(forward, Vector3.up)`, giving a
 * quaternion still in **Unity's** convention — feed it to `convertQuaternion`
 * to draw with it.
 *
 *     z = forward.normalized
 *     x = Vector3.Cross(up, z).normalized
 *     y = Vector3.Cross(z, x)
 *     q = quaternion of the basis [x y z]
 *
 * The cross product and the matrix-to-quaternion formula are written the same
 * way in Unity and three.js — the handedness difference lives entirely in what
 * the axes *mean*, not in the algebra — so building the basis with three's own
 * vector maths reproduces Unity's answer component for component. Returns
 * identity for a degenerate forward, as Unity does.
 */
export function unityLookRotation(forward: Vector3, out = new Quaternion()): Quaternion {
  _z.copy(forward)
  if (_z.lengthSq() < 1e-12) return out.identity()
  _z.normalize()
  _x.crossVectors(_up, _z)
  if (_x.lengthSq() < 1e-12) return out.identity()
  _x.normalize()
  _y.crossVectors(_z, _x)
  _basis.makeBasis(_x, _y, _z)
  return out.setFromRotationMatrix(_basis)
}
